import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Switch,
  Alert,
  Box,
  Grid
} from '@mui/material';
import { WalteroButton, WalteroTypography } from '../waltero';
import { subscriptionService } from '../../services/subscriptionService';

const CreateSubscriptionTypeModal = ({ open, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    durationDays: '',
    basePrice: '',
    gracePeriodDays: '',
    isActive: true
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (field) => (event) => {
    const value = field === 'isActive' ? event.target.checked : event.target.value;
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    try {
      setLoading(true);
      setError(null);

      // Prepare data for submission
      const submitData = {
        ...formData,
        durationDays: parseInt(formData.durationDays) || 0,
        basePrice: formData.basePrice ? parseFloat(formData.basePrice) : null,
        gracePeriodDays: formData.gracePeriodDays === '' ? 0 : Math.max(0, parseInt(formData.gracePeriodDays, 10) || 0)
      };

      // Validate required fields
      if (!submitData.name.trim()) {
        throw new Error('Name is required');
      }
      if (!submitData.durationDays || submitData.durationDays <= 0) {
        throw new Error('Duration must be greater than 0 days');
      }

      await subscriptionService.createSubscriptionType(submitData);
      
      // Reset form and close modal
      setFormData({
        name: '',
        description: '',
        durationDays: '',
        basePrice: '',
        gracePeriodDays: '',
        isActive: true
      });
      
      onSuccess?.();
      onClose();
      
    } catch (err) {
      console.error('Error creating subscription type:', err);
      setError(err.message || 'Failed to create subscription type');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setFormData({
        name: '',
        description: '',
        durationDays: '',
        basePrice: '',
        gracePeriodDays: '',
        isActive: true
      });
      setError(null);
      onClose();
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <WalteroTypography 
          variant="h5" 
          sx={{ 
            color: 'waltero.primary.coreBlue',
            fontWeight: 600 
          }}
        >
          Create Subscription Type
        </WalteroTypography>
      </DialogTitle>
      
      <form onSubmit={handleSubmit}>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}
          
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Name *"
                value={formData.name}
                onChange={handleChange('name')}
                disabled={loading}
                placeholder="e.g., Basic Plan, Premium Plan"
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={formData.description}
                onChange={handleChange('description')}
                disabled={loading}
                multiline
                rows={2}
                placeholder="Brief description of this subscription type"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Duration (Days) *"
                type="number"
                value={formData.durationDays}
                onChange={handleChange('durationDays')}
                disabled={loading}
                inputProps={{ min: 1 }}
                placeholder="e.g., 30, 365"
                helperText="Number of days this subscription lasts"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Base Price (per sensor / month)"
                type="number"
                value={formData.basePrice}
                onChange={handleChange('basePrice')}
                disabled={loading}
                inputProps={{ min: 0, step: 0.01 }}
                placeholder="e.g., 29.99"
                helperText="Charge applied per sensor each month. Leave empty for custom pricing."
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Grace Period (Days)"
                type="number"
                value={formData.gracePeriodDays}
                onChange={handleChange('gracePeriodDays')}
                disabled={loading}
                inputProps={{ min: 0 }}
                placeholder="e.g., 14"
                helperText="Number of days sensors can run before billing begins."
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <Box sx={{ pt: 2 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isActive}
                      onChange={handleChange('isActive')}
                      disabled={loading}
                      color="primary"
                    />
                  }
                  label="Active"
                />
                <WalteroTypography 
                  variant="caption" 
                  sx={{ display: 'block', color: 'text.secondary', mt: 0.5 }}
                >
                  Active subscription types can be assigned to organizations
                </WalteroTypography>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <WalteroButton 
            onClick={handleClose}
            disabled={loading}
            variant="outlined"
          >
            CANCEL
          </WalteroButton>
          <WalteroButton 
            type="submit"
            disabled={loading}
            variant="contained"
          >
            {loading ? 'CREATING...' : 'CREATE TYPE'}
          </WalteroButton>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default CreateSubscriptionTypeModal;
